/**
 * mSTEM Shared Projects Modal container (PRJ-05 / LLD UC 12).
 *
 * Owns local state: searchTerm + appliedTerm (200ms debounce) + sourceFilter
 * + loadingError. Sorts the redux-stored sharedProjects list by sharedAt
 * DESC; filters by source ('teacher' | 'class' | 'community' | 'all') and
 * 200ms-debounced search term (case-insensitive substring match against
 * name + sharedBy).
 *
 * Row handlers (DP-07):
 *   handleView(id)
 *     - Fake-load: vm.loadProject(defaultProject()) — read-only viewing,
 *       no setCurrentProject (so a subsequent Save won't write to the
 *       shared entry's id).
 *     - On resolve: setProjectTitle(entry.name) + sharedViewed toast +
 *       close the modal.
 *     - On reject: projectLoadError toast + loadingError state set.
 *
 *   handleSaveCopy(id)
 *     - Defense-in-depth: re-check entry.permission === 'copy' before
 *       persisting (button is already conditionally rendered, but a
 *       malicious caller could still invoke the handler via devtools).
 *     - createMyProject({name: '[Copy] <original>', type, description}) +
 *       writeMyProjects → on quota failure: localStorageFull toast.
 *     - On success: dispatch createProjectEntry + sharedCopied toast.
 *       Modal stays open per LLD UC 12 so user can browse more shares.
 *
 * Search debounce cleanup: componentWillUnmount cancels the lodash.debounce
 * wrapper to avoid React 16 setState-on-unmounted-component warning.
 *
 * W-9 fix: intl propType omitted — handler body never reads this.props.intl
 * (only the inner presentational component, separately wrapped in
 * injectIntl, consumes intl). compose(injectIntl, connect) wrap retained
 * for symmetry with Plans 03-02/03/04/05.
 */
import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';
import VM from 'openblock-vm';

import {closeSharedProjectsModal} from '../reducers/modals';
import {createProjectEntry} from '../reducers/my-projects';
import {setProjectTitle} from '../reducers/project-title';
import {showAlertWithTimeout} from '../reducers/alerts';
import {createMyProject, writeMyProjects} from '../lib/mstem-project-storage';
import defaultProject from '../lib/default-project';
import log from '../lib/log';

import SharedProjectsModalComponent from '../components/shared-projects-modal/shared-projects-modal.jsx';

const SEARCH_DEBOUNCE_MS = 200;

const sortBySharedDesc = function (list) {
    return list.slice().sort((a, b) => {
        const ta = new Date(a && a.sharedAt ? a.sharedAt : 0).getTime();
        const tb = new Date(b && b.sharedAt ? b.sharedAt : 0).getTime();
        return tb - ta;
    });
};

const applyFilters = function (list, term, source) {
    let out = list;
    if (source && source !== 'all') {
        out = out.filter(p => p.sharedFrom === source);
    }
    const t = (term || '').trim().toLowerCase();
    if (t) {
        out = out.filter(p => {
            const inName = (p.name || '').toLowerCase().indexOf(t) >= 0;
            const inSharedBy = (p.sharedBy || '').toLowerCase().indexOf(t) >= 0;
            return inName || inSharedBy;
        });
    }
    return out;
};

class SharedProjectsModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSearch',
            'applySearch',
            'handleFilter',
            'handleView',
            'handleSaveCopy',
            'handleClose'
        ]);
        this.applySearchDebounced = debounce(this.applySearch, SEARCH_DEBOUNCE_MS);
        this.state = {
            searchTerm: '',
            appliedTerm: '',
            sourceFilter: 'all',
            loadingError: false
        };
    }
    componentWillUnmount () {
        if (this.applySearchDebounced && this.applySearchDebounced.cancel) {
            this.applySearchDebounced.cancel();
        }
    }
    handleSearch (e) {
        const value = e.target.value;
        this.setState({searchTerm: value});
        this.applySearchDebounced(value);
    }
    applySearch (value) {
        this.setState({appliedTerm: value});
    }
    handleFilter (e) {
        this.setState({sourceFilter: e.target.value});
    }
    handleView (id) {
        const entry = this.props.sharedProjects.find(p => p.id === id);
        if (!entry) {
            this.setState({loadingError: true});
            this.props.onShowLoadError();
            return;
        }
        const onSuccess = () => {
            this.props.onSetProjectTitle(entry.name);
            // DO NOT setCurrentProject — this is read-only view, no save target.
            this.props.onShowViewed();
            this.props.onClose();
        };
        const onError = err => {
            log.warn('SharedProjectsModal vm.loadProject error', err);
            this.setState({loadingError: true});
            this.props.onShowLoadError();
        };
        try {
            // MICRO-3.1 BUG FIX: defaultProject() returns an asset LIST, but
            // vm.loadProject expects a JSON string/buffer of just the project.
            // Pass assets[0].data (the project JSON) so the read-only fake-load
            // resolves instead of always rejecting with a "Không thể tải dự án"
            // toast. Same root cause as Bug 1 / Bug 2 in micro-3.1.
            const assets = defaultProject();
            const projectJson = assets && assets[0] && assets[0].data;
            this.props.vm.loadProject(projectJson).then(onSuccess, onError);
        } catch (err) {
            onError(err);
        }
    }
    handleSaveCopy (id) {
        const entry = this.props.sharedProjects.find(p => p.id === id);
        if (!entry) return;
        // Defense-in-depth (T-03-06-01): button is conditionally rendered,
        // but a malicious caller could still invoke this handler directly.
        if (entry.permission !== 'copy') return;

        const copyEntry = createMyProject({
            name: `[Copy] ${entry.name}`,
            type: entry.type,
            description: entry.description || ''
        });
        const nextList = this.props.myProjectsList.concat([copyEntry]);

        let quotaHit = false;
        const ok = writeMyProjects(nextList, () => {
            quotaHit = true;
        });
        if (!ok || quotaHit) {
            this.props.onShowQuotaFull();
            return;
        }
        this.props.onCreateEntry(copyEntry);
        this.props.onShowCopied();
    }
    handleClose () {
        this.props.onClose();
    }
    render () {
        const sorted = sortBySharedDesc(this.props.sharedProjects);
        const filtered = applyFilters(sorted, this.state.appliedTerm, this.state.sourceFilter);
        return (
            <SharedProjectsModalComponent
                filteredProjects={filtered}
                loadingError={this.state.loadingError}
                searchTerm={this.state.searchTerm}
                sourceFilter={this.state.sourceFilter}
                onClose={this.handleClose}
                onFilter={this.handleFilter}
                onSaveCopy={this.handleSaveCopy}
                onSearch={this.handleSearch}
                onView={this.handleView}
            />
        );
    }
}

SharedProjectsModal.propTypes = {
    myProjectsList: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    onClose: PropTypes.func.isRequired,
    onCreateEntry: PropTypes.func.isRequired,
    onSetProjectTitle: PropTypes.func.isRequired,
    onShowCopied: PropTypes.func.isRequired,
    onShowLoadError: PropTypes.func.isRequired,
    onShowQuotaFull: PropTypes.func.isRequired,
    onShowViewed: PropTypes.func.isRequired,
    sharedProjects: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    myProjectsList: state.scratchGui.myProjects.list,
    sharedProjects: state.scratchGui.sharedProjects.list,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeSharedProjectsModal()),
    onCreateEntry: entry => dispatch(createProjectEntry(entry)),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    onShowCopied: () => showAlertWithTimeout(dispatch, 'sharedCopied'),
    onShowViewed: () => showAlertWithTimeout(dispatch, 'sharedViewed'),
    onShowLoadError: () => showAlertWithTimeout(dispatch, 'projectLoadError'),
    onShowQuotaFull: () => showAlertWithTimeout(dispatch, 'localStorageFull')
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(SharedProjectsModal);
